import { useState, useMemo } from 'react';

export type SortDirection = 'asc' | 'desc' | null;

export interface SortState {
  column: string | null;
  direction: SortDirection;
}

export interface UseTableSortingProps<T> {
  data: T[];
  defaultSort?: SortState;
}

export function useTableSorting<T>({ data, defaultSort }: UseTableSortingProps<T>) {
  const [sortState, setSortState] = useState<SortState>(
    defaultSort || { column: null, direction: null }
  );

  const handleSort = (column: string) => {
    setSortState(prevState => {
      if (prevState.column === column) {
        // Cycle through: asc -> desc -> null
        const newDirection: SortDirection = 
          prevState.direction === 'asc' ? 'desc' : 
          prevState.direction === 'desc' ? null : 'asc';
        
        return {
          column: newDirection ? column : null,
          direction: newDirection
        };
      } else {
        // New column, start with ascending
        return {
          column,
          direction: 'asc'
        };
      }
    });
  };

  const sortedData = useMemo(() => {
    if (!sortState.column || !sortState.direction) {
      return data;
    }

    return [...data].sort((a, b) => {
      const aValue = getNestedValue(a, sortState.column!);
      const bValue = getNestedValue(b, sortState.column!);
      
      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return sortState.direction === 'asc' ? 1 : -1;
      if (bValue == null) return sortState.direction === 'asc' ? -1 : 1;

      // Enhanced date handling for posting_date and other date fields
      if (sortState.column === 'posting_date' || sortState.column === 'created_at' || sortState.column === 'updated_at') {
        return compareDates(aValue, bValue, sortState.direction);
      }

      // Handle numeric columns
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        const comparison = aValue - bValue;
        return sortState.direction === 'asc' ? comparison : -comparison;
      }

      // Convert to strings for comparison if not already
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      const comparison = aStr.localeCompare(bStr, undefined, { 
        numeric: true, 
        sensitivity: 'base' 
      });

      return sortState.direction === 'asc' ? comparison : -comparison;
    });
  }, [data, sortState]);

  return {
    sortState,
    handleSort,
    sortedData
  };
}

// Helper function to get nested object values
function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj);
}

// Enhanced date comparison function
function compareDates(aValue: any, bValue: any, direction: SortDirection): number {
  let aDate: Date;
  let bDate: Date;

  // Handle different date formats
  if (aValue instanceof Date) {
    aDate = aValue;
  } else if (typeof aValue === 'string') {
    // Handle various date string formats
    if (aValue.includes('T') || aValue.includes('Z')) {
      // ISO format or full datetime
      aDate = new Date(aValue);
    } else if (aValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // YYYY-MM-DD format - parse without timezone conversion
      const [year, month, day] = aValue.split('-').map(Number);
      aDate = new Date(year, month - 1, day);
    } else {
      // Other string formats
      aDate = new Date(aValue);
    }
  } else {
    aDate = new Date(aValue);
  }

  if (bValue instanceof Date) {
    bDate = bValue;
  } else if (typeof bValue === 'string') {
    // Handle various date string formats
    if (bValue.includes('T') || bValue.includes('Z')) {
      // ISO format or full datetime
      bDate = new Date(bValue);
    } else if (bValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // YYYY-MM-DD format - parse without timezone conversion
      const [year, month, day] = bValue.split('-').map(Number);
      bDate = new Date(year, month - 1, day);
    } else {
      // Other string formats
      bDate = new Date(bValue);
    }
  } else {
    bDate = new Date(bValue);
  }

  // Handle invalid dates
  const aIsValid = !isNaN(aDate.getTime());
  const bIsValid = !isNaN(bDate.getTime());

  if (!aIsValid && !bIsValid) return 0;
  if (!aIsValid) return direction === 'asc' ? 1 : -1;
  if (!bIsValid) return direction === 'asc' ? -1 : 1;

  // Compare valid dates
  const comparison = aDate.getTime() - bDate.getTime();
  return direction === 'asc' ? comparison : -comparison;
}
